<template>
  <div>
    <b-card title="Error Response">
      <!-- search input -->
      <b-row class="mb-1">
        <!-- search input -->
        <b-col>
          <section>
            <b-button
              v-b-modal.modal-filter-orders
              variant="outline-primary"
              class="d-flex"
            >
              <feather-icon icon="FilterIcon" />Filter
            </b-button>
          </section>
        </b-col>
        <b-col>
          <b-form-group
            v-if="reconciliations.length != 0"
            class="d-flex justify-content-center"
          >
            <!-- search input -->
            <div>
              <b-form-input
                v-model="searchTerm"
                type="search"
                placeholder="search"
              />
            </div>
          </b-form-group>
        </b-col>

        <b-col
          v-if="sheets[0].data.length != 0"
          class="d-flex justify-content-end"
        >
          <xlsx-workbook>
            <xlsx-sheet
              v-for="sheet in sheets"
              :key="sheet.name"
              :collection="sheet.data"
              :sheet-name="sheet.name"
            />
            <xlsx-download>
              <b-button variant="primary" class="d-flex">
                <feather-icon icon="DownloadCloudIcon" /> Export
              </b-button>
            </xlsx-download>
          </xlsx-workbook>
        </b-col>
      </b-row>

      <div>
        <!-- table -->
        <vue-good-table
          :columns="columns"
          :rows="reconciliations"
          :rtl="direction"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm,
          }"
          :pagination-options="{
            enabled: true,
            perPage: pageLength,
          }"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'description'">
              <p v-if="props.row.responseCode === 200">
                Success / Notification already received
              </p>

              <span v-else-if="props.row.responseCode === 400">
                Bad Request
              </span>
              <span v-else-if="props.row.responseCode === 401">
                Unauthorized Request
              </span>
              <span v-else-if="props.row.responseCode === 404">
                Order not Found
              </span>
              <span v-else-if="props.row.responseCode === 412">
                Unable to get Query Order Response
              </span>
              <span v-else-if="props.row.responseCode === 500">
                Internal Server Error
              </span>
              <span v-else-if="props.row.responseCode === 503">
                Service Unavailable
              </span>
              <span v-else>
                {{ props.row.responseCode }}
              </span>
            </span>
            <span v-else-if="props.column.field === 'action'">
              <span>
                <b-link
                  :to="{
                    name: 'merchantResponseGroupDetail-view',
                    params: {
                      responseCode: props.row.responseCode,
                      fromDate: props.row.fromDate,
                      toDate: props.row.toDate,
                      merchantIds: props.row.merchantIds,
                    },
                  }"
                  target="_blank"
                >
                  <span>Details</span>
                </b-link>
              </span>
            </span>
          </template>
          <!-- pagination -->
          <template slot="pagination-bottom" slot-scope="props">
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-nowrap"> Showing 1 to </span>
                <b-form-select
                  v-model="pageLength"
                  :options="['5', '10', '15', '30', '50']"
                  class="mx-1"
                  @input="
                    (value) => props.perPageChanged({ currentPerPage: value })
                  "
                />
                <span class="text-nowrap"> of {{ props.total }} entries </span>
              </div>
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="props.total"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value) => props.pageChanged({ currentPage: value })"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>

        <div>
          <b-modal
            id="modal-filter-orders"
            title="Filter Merchant Response"
            ok-title="Search"
            cancel-variant="outline-secondary"
            size="lg"
            @ok="filterException"
          >
            <div>
              <b-form>
                <b-row style="height: 300px" class="overflow-auto">
                  <b-col cols="8" class="mx-auto">
                    <b-form-group label="Merchants option">
                      <v-select
                        multiple
                        v-model="selected"
                        :options="merchants"
                        label="name"
                      >
                        <template #option="option">
                          <span>{{ option.name }}</span>
                        </template>
                      </v-select>
                    </b-form-group>
                    <b-form-group label="Paid Date Range">
                      <flat-pickr
                        v-model="trxnDateRange"
                        class="form-control"
                        :config="{ mode: 'range', maxDate: 'today' }"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-form>
            </div>
          </b-modal>
        </div>

        <!-- loadings -->
        <b-overlay :show="isLoading" no-wrap rounded="sm" />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BTabs,
  BTab,
  BCard,
  BButton,
  BBadge,
  BPagination,
  BOverlay,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormDatepicker,
  BRow,
  BCol,
  BForm,
  BTable,
  BCardText,
  BFormCheckbox,
  BLink,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axios from "@axios";
import store from "@/store";
import flatPickr from "vue-flatpickr-component";
import BCardActions from "@core/components/b-card-actions/BCardActions.vue";
import XlsxSheet from "vue-xlsx/dist/components/XlsxSheet";
import XlsxDownload from "vue-xlsx/dist/components/XlsxDownload";
import XlsxWorkbook from "vue-xlsx/dist/components/XlsxWorkbook";
import vSelect from "vue-select";
export default {
  components: {
    BTabs,
    BTab,
    BRow,
    BCol,
    BCard,
    VueGoodTable,
    BBadge,
    BButton,
    BOverlay,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormDatepicker,
    flatPickr,
    BForm,
    BTable,
    BCardText,
    BCardActions,
    BFormCheckbox,
    BLink,
    XlsxWorkbook,
    XlsxSheet,
    XlsxDownload,
    vSelect,
  },
  data() {
    const nowDate = new Date();
    return {
      selected: [],
      merchantIds: "",
      merchants: [],
      isLoading: false,
      dataFiltered: false,
      rows: [],
      pageLength: 5,
      isDeleteModel: false,
      dir: false,
      rangeDate: null,
      activeRow: null,
      extendedDate: `${nowDate.getUTCFullYear()}-${
        nowDate.getUTCMonth() + 1
      }-${nowDate.getUTCDate()}`,
      orderDateRange: "",
      trxnDateRange: "",
      searchTerm: "",
      VendorTxnRefNumber: "",
      orderStartDate: "",
      trxnStartDate: "",
      orderEndDate: "",
      trxnEndDate: "",
      orderFirstName: "",
      orderLastName: "",
      orderEmail: "",
      orderPhone: "",
      orderAmount: "",
      orderCurrency: "",
      orderCity: "",
      orderCountry: "",
      orderChannel: "",
      selectedOrder: "",
      filterOrderId: "",
      filterFirstName: "",
      filterLastName: "",
      filtertrnxDate: "",
      columns: [
        {
          label: "Merchant Response Code",
          field: "responseCode",
        },
        {
          label: "From Date",
          field: "fromDate",
        },
        {
          label: "To Date",
          field: "toDate",
        },
        {
          label: "Description",
          field: "description",
        },
        {
          label: "Count",
          field: "responsesCount",
        },
        {
          label: "Action",
          field: "action",
        },
      ],
      paymentOptions: [{ value: 0, text: "--select Payment Option--" }],

      reconciliations: [],
      sheets: [{ name: "Detail Transactions", data: [] }],
    };
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        1: "light-success",
        0: "light-danger",
        /* eslint-enable key-spacing */
      };

      return (status) => statusColor[status];
    },
  },
  created() {
    this.fetchMerchant();
    this.fetchallPaymentOptions();
  },
  beforeCreate() {
    this.$http.get("/account-setting/data").then((res) => {
      this.options = res.data;
    });
  },
  methods: {
    fetchMerchant() {
      this.isLoading = true;
      this.$store.dispatch(
        "config/getActionPrivilege",
        "Report-MerchantResponseGroup"
      );
      axios
        .get("/Merchant/api/V1.0/Merchants/GetMerchants")
        .then((res) => {
          this.merchants = res.merchants;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    fetchallPaymentOptions() {
      this.isLoading = true;
      this.$store.dispatch(
        "config/getActionPrivilege",
        "Report-MerchantResponseGroup"
      );
      axios
        .get(
          "/PaymentOptions/api/V1.0/PaymentOptions/ViewMerchantPaymentOptions"
        )
        .then((res) => {
          res.paymentOptions.forEach((item) => {
            this.paymentOptions.push({
              value: item.id,
              text: item.name,
            });
          });
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    filterException() {
      this.selected.map((m) => {
        this.merchantIds += m.id + ",";
      });
      this.merchantIds = this.merchantIds.slice(0, -1);
      if (this.orderDateRange !== "") {
        const [orderSttDate, orderEdDate] = this.orderDateRange.split(" to ");

        if (this.orderDateRange.includes("to")) {
          this.orderStartDate = orderSttDate;
          this.orderEndDate = orderEdDate;
        } else {
          this.orderStartDate = this.orderDateRange;
          this.orderEndDate = this.orderDateRange;
        }
      }

      if (this.trxnDateRange !== "") {
        const [trxnSttDate, trxnEdDate] = this.trxnDateRange.split(" to ");
        // this.trxnEndDate = `${trxnEdDate.trim()}`
        // this.trxnStartDate = `${trxnSttDate.trim()}`

        if (this.trxnDateRange.includes("to")) {
          this.trxnStartDate = trxnSttDate;
          this.trxnEndDate = trxnEdDate;
        } else {
          this.trxnStartDate = this.trxnDateRange;
          this.trxnEndDate = this.trxnDateRange;
        }
      }

      this.isLoading = true;
      this.$store.dispatch(
        "config/getActionPrivilege",
        "Report-MerchantResponseGroup"
      );
      const merchantIds = this.merchantIds;
      console.log(this.trxnEndDate);
      const url = `/Order/api/V1.0/Report/MerchantResponseGroup?transactionStartDate=${this.trxnStartDate}&transactionEndDate=${this.trxnEndDate}&merchantIds=${this.merchantIds}`;
      // Api call
      axios
        .get(url)
        .then((res) => {
          this.sheets[0].data = [];

          this.dataFiltered = true;

          this.reconciliations = res.merchantResponseGroupingResponse;
          this.reconciliations.map((m) => {
            m.merchantIds = merchantIds;
          });
          res.merchantResponseGroupingResponse.map((m) => {
            this.sheets[0].data.push({
              "Merchant Response Code": m.merchantResponseCode,
              "From Date": m.fromDate,
              "To Date": m.toDate,
              Description: m.description,
              Count: m.responsesCount,
            });
          });
          this.isLoading = false;
        })
        .catch((res) => {
          console.log(res);
          this.isLoading = false;
          this.reconciliations = [];
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: `${res.data.message}`,
            },
          });
          // this.$refs.loginForm.setErrors(error)
        });
      this.merchantIds = "";
      this.trxnEndDate = "";
      this.trxnStartDate = "";
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss">
@import "@core/scss/vue/pages/page-pricing.scss";
</style>
